import type { GatewayModelChoice } from "../chat/delegate-router.ts";
import type { GatewayBrowserClient } from "../gateway.ts";

export type ChatModelsState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  chatModelCatalog: GatewayModelChoice[];
  chatModelCatalogLoading: boolean;
  chatModelCatalogError: string | null;
};

export async function loadChatModelCatalog(state: ChatModelsState) {
  if (!state.client || !state.connected) {
    return;
  }
  if (state.chatModelCatalogLoading) {
    return;
  }
  state.chatModelCatalogLoading = true;
  state.chatModelCatalogError = null;
  try {
    const listed = await state.client.request<{
      models?: Array<{ id: string; name?: string; provider: string }>;
    }>("models.list", {});
    const raw = Array.isArray(listed?.models) ? listed.models : [];
    state.chatModelCatalog = raw.map((entry) => ({
      id: entry.id,
      provider: entry.provider,
      name: entry.name?.trim() ? entry.name : entry.id,
    }));
  } catch (err) {
    state.chatModelCatalogError = String(err);
    state.chatModelCatalog = [];
  } finally {
    state.chatModelCatalogLoading = false;
  }
}

export function modelRefFromChoice(choice: GatewayModelChoice): string {
  return `${choice.provider}/${choice.id}`;
}

export type ModelProviderGroup = {
  provider: string;
  label: string;
  models: GatewayModelChoice[];
};

const LOCAL_PROVIDER_IDS = new Set(["ollama"]);

export function groupModelsByProvider(models: GatewayModelChoice[]): ModelProviderGroup[] {
  const map = new Map<string, GatewayModelChoice[]>();
  const installedLocal: GatewayModelChoice[] = [];
  for (const model of models) {
    const provider = model.provider || "unknown";
    if (LOCAL_PROVIDER_IDS.has(provider.toLowerCase())) {
      installedLocal.push(model);
      continue;
    }
    const list = map.get(provider) ?? [];
    list.push(model);
    map.set(provider, list);
  }

  const groups = [...map.entries()]
    .map(([provider, entries]) => ({
      provider,
      label: provider,
      models: entries.toSorted((a, b) => a.name.localeCompare(b.name)),
    }))
    .toSorted((a, b) => a.provider.localeCompare(b.provider));

  if (installedLocal.length > 0) {
    return [
      {
        provider: "installed-local",
        label: "Installed (local)",
        models: installedLocal.toSorted((a, b) => a.name.localeCompare(b.name)),
      },
      ...groups,
    ];
  }
  return groups;
}
